/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {createAction} from "helpers/createAction";
import {UserType} from "types/enums/UserType";
import {Reservation} from "types/entities/Reservation";
import ajax from "helpers/ajax";
import {AccountDriver} from "types/entities/AccountDriver";
import {ReservationDriver} from "types/entities/ReservationDriver";
import SessionAction from "store/session/SessionAction";
import {ReservationOfferStatus} from "types/enums/ReservationOfferStatus";
import {ColumnFiltersState} from "@tanstack/react-table";
import {DEFAULT_HALL_ID} from "helpers/config";

export interface UpdateCustomerFromReservationRequest {
    customer: BookingLoginCustomerInReservation;
    reservation: Reservation;
}

export interface ReservationInvitationRequest {
    emails: string;
    reservation_id: number;
}

export interface ReservationInvitationGuestRequest {
    reservation_driver_guid: string;
    name: string;
    last_name: string;
    nickname: string;
    birthdate: string;
    address: string;
    zip: string;
    town: string;
    country: string;
    email: string;
    phone: string;
}

export interface ReservationImportGuestsFromFileRequest {
    file: FormData;
    reservation_id: number;
}

export interface BookingLoginCustomerInReservation {
    driver_id?: number;
    user_type: UserType | string;
    name: string;
    last_name: string;
    nickname: string;
    birthdate: string;
    email?: string;
    phone?: string;
    reservation_driver_id?: number;
}

export interface SetCustomerToReservationRequest {
    customer: BookingLoginCustomerInReservation;
    reservation: Reservation;
}

export interface ExcelImportResponse {
    importState: {
        importCollection: [];
        failureCollection: [];
        importRows: number;
        totalRows: number;
    };
}

export enum ReservationsTableSort {
    Asc = "asc",
    Desc = "desc",
}

export enum ReservationsTableSortBy {
    // Package = "package",
    // Company = "company",
    // PersonCount = "person_count",
    Date = "date",
    // Email = "email",
    Status = "status",
    // Guid = "guid",
    CreatedAt = "created_at",
    CreatedBy = "created_by",
}

export interface ReservationsTableFilter {
    // year: string;
    // month: string;
    // day?: string;
    // package?: number;
    // company?: string;
    // person_count?: {min?: number; max?: number};
    // date?: string;
    // email?: string;
    // status?: ReservationStatus;
    // guid?: string;
    // created_at?: string;
}

export interface ReservationsTableRequest {
    filter?: ReservationsTableFilter;
    page?: number; // page
    per_page?: number; // per_page
    // search?: string;
    // search?: FilterSearch[];
    search?: ColumnFiltersState | null;
    sort?: ReservationsTableSort | null;
    sort_by?: ReservationsTableSortBy | null;
}

class BookingLoginAction {
    static readonly SET_BOOKING_LOGIN_RESERVATION = "BookingLoginAction.SetReservation";
    static readonly SET_BOOKING_LOGIN_USER = "BookingLoginAction.SetUser";
    static readonly SET_BOOKING_LOGIN_RESERVATIONS = "BookingLoginAction.SetReservations";
    static readonly CLEAR_BOOKING_LOGIN = "BookingLoginAction.ClearBookingLogin";

    static fetchCheckCustomerEmail = (email: string): ((dispatch, getState) => Promise<any>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    exists: boolean;
                } = await ajax.post(
                    "/user/exists-email",
                    {
                        email: email,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response.exists === true) {
                    console.log("fetchLoginCheck - true");
                    return Promise.resolve(response.exists);
                } else if (response.exists === false) {
                    console.log("fetchLoginCheck - false");
                    return Promise.resolve(response.exists);
                } else {
                    return Promise.reject("error");
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchSetCustomerToReservation = (data: SetCustomerToReservationRequest): ((dispatch, getState) => Promise<Reservation>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservation: Reservation;
                    reason?: string;
                } = await ajax.post(
                    "/online-venue/reservation/guests/" + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservation) {
                    dispatch(BookingLoginAction.setReservation(response.reservation));
                    return Promise.resolve(response.reservation);
                } else {
                    return Promise.reject(response.reason);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchUpdateCustomerFromReservation = (data: UpdateCustomerFromReservationRequest): ((dispatch, getState) => Promise<Reservation>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservation: Reservation;
                    reason?: string;
                } = await ajax.post(
                    "/online-venue/reservation/guests/" + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservation) {
                    dispatch(BookingLoginAction.setReservation(response.reservation));
                    return Promise.resolve(response.reservation);
                } else {
                    return Promise.reject(response.reason);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchAddOrEditCartItems = (reservation_id: number, reservation_driver_id: number, packages_cart_item_id: number, type: "add" | "remove") => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservation: Reservation;
                    reservationDriver: ReservationDriver;
                } = await ajax.post(
                    "/online-venue/reservation/guests/" + reservation_id + "/cart-items",
                    {
                        type: type,
                        reservation_driver_id: reservation_driver_id,
                        packages_cart_item_id: packages_cart_item_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );
                console.log("fetchAddOrEditCartItems", response);
                return Promise.resolve(response);
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationInvitation = (data: ReservationInvitationRequest): ((dispatch, getState) => Promise<any>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    emails: [];
                } = await ajax.post(
                    "/online-venue/reservation/invitation/" + data.reservation_id,
                    {
                        emails: data.emails,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                console.log("fetchReservationInvitation", response);

                if (response?.emails) {
                    return Promise.resolve(response.emails);
                } else {
                    return Promise.reject(response);
                }
            } catch (error) {
                console.log("fetchReservationInvitation - error", error);
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationInvitationGuest = (data: ReservationInvitationGuestRequest): ((dispatch) => Promise<any>) => {
        return async dispatch => {
            try {
                const response: {
                    data: any;
                } = await ajax.post("/online-venue/reservation/guest/" + data.reservation_driver_guid, {
                    ...data,
                });

                console.log("fetchReservationInvitationGuest", response);
                return Promise.resolve(response.data);

                // if (response?.reservation) {
                //     dispatch(BookingLoginAction.setReservation(response.reservation));
                //     return Promise.resolve(response.reservation);
                // } else {
                //     return Promise.reject(response.reason);
                // }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static getReservationInvitationGuest = (reservation_driver_guid: string): ((dispatch) => Promise<ReservationDriver>) => {
        return async dispatch => {
            try {
                const response: {
                    reservationDriver: ReservationDriver;
                } = await ajax.get("/online-venue/reservation/guest/" + reservation_driver_guid);

                console.log("fetchReservationInvitationGuest", response);
                return Promise.resolve(response.reservationDriver);
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationImportGuestsFromFile = (data: ReservationImportGuestsFromFileRequest): ((dispatch, getState) => Promise<ExcelImportResponse>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: ExcelImportResponse = await ajax.post("/online-venue/reservation/guest-import/" + data.reservation_id, data.file, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });

                if (response.importState.failureCollection.length === 0) {
                    console.log("on resolve from fetchReservationImportGuestsFromFile", response);
                    return Promise.resolve(response);
                } else {
                    console.log("on reject from fetchReservationImportGuestsFromFile", response);
                    return Promise.reject(response);
                }
            } catch (error) {
                console.error(error);
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationDetail = (reservation_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            return new Promise(async (resolve, reject) => {
                try {
                    const response: {
                        reservation: Reservation;
                    } = await ajax.get("/online-venue/reservation/detail/" + reservation_id, {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    });

                    if (response?.reservation) {
                        dispatch(BookingLoginAction.setReservation(response.reservation));
                        resolve(response.reservation);
                    } else {
                        dispatch(BookingLoginAction.clearReservations());
                        return response;
                    }
                } catch (error) {
                    reject(error);
                }
            });
        };
    };

    static fetchSetReservationOfferStatus = (reservation_id: number, offerStatus: ReservationOfferStatus): ((dispatch) => Promise<any>) => {
        return async dispatch => {
            try {
                const response: {
                    reservation: Reservation;
                } = await ajax.post("/online-venue/reservation/seller/set_reservation_offer/" + reservation_id, {offer: offerStatus});
                dispatch(BookingLoginAction.setReservation(response.reservation));
                return Promise.resolve(response);
            } catch (error) {
                console.error(error);
                return Promise.reject(error);
            }
        };
    };
    static fetchSellerReservationDetail = (reservation_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            return new Promise(async (resolve, reject) => {
                try {
                    const response: {
                        reservation: Reservation;
                    } = await ajax.get("/online-venue/reservation/seller/detail/" + reservation_id, {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    });

                    if (response?.reservation) {
                        dispatch(BookingLoginAction.setReservation(response.reservation));
                        resolve(response.reservation);
                        return response;
                    } else {
                        dispatch(BookingLoginAction.clearReservations());
                        return response;
                    }
                } catch (error) {
                    reject(error);
                }
            });
        };
    };

    static fetchReservationsList = () => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservations: Reservation[];
                } = await ajax.get("/online-venue/reservations/list", {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });
                if (response?.reservations) {
                    dispatch(BookingLoginAction.setReservations(response.reservations));
                    return Promise.resolve(response.reservations);
                } else {
                    return Promise.reject(response);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationsListForSeller = (seller_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservations: Reservation[];
                } = await ajax.post(
                    "/online-venue/reservation/seller/list",
                    {
                        seller_id: seller_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );
                if (response?.reservations) {
                    dispatch(BookingLoginAction.setReservations(response.reservations));
                    return Promise.resolve(response.reservations);
                } else {
                    return Promise.reject(response);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationsListForSellerWithPagination = (seller_id: number, data: ReservationsTableRequest) => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;
            return ajax
                .post(
                    // "/admin/reservations/table/all",
                    "/online-venue/reservation/seller/pagination",
                    {
                        hall_id: DEFAULT_HALL_ID,
                        per_page: data?.per_page,
                        page: data?.page,
                        search: data?.search && data.search,
                        sort: data?.sort && data.sort,
                        sort_by: data?.sort_by && data.sort_by,
                        seller_id: seller_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                )
                .then(
                    onResolve => {
                        return onResolve.reservations;
                    },
                    onReject => {
                        console.log("fetch data error");
                        console.log(onReject);
                    }
                )
                .catch(error => {
                    if (error.name === "CanceledError") {
                        console.log("Request aborted:", error.message);
                    } else {
                        console.error(error);
                    }
                    return error;
                });
        };

        // return (dispatch, getState) => {
        //     const session = getState().sessionState.session;
        //     return ajax
        //         .post(
        //             "/online-venue/reservation/seller/list",
        //             {
        //                 // TODO: odkomentovat ked bude api pripravene
        //                 // hall_id: DEFAULT_HALL_ID,
        //                 // per_page: data?.per_page,
        //                 // page: data?.page,
        //                 // search: data?.search && data.search,
        //                 // sort: data?.sort && data.sort,
        //                 // sort_by: data?.sort_by && data.sort_by,
        //                 seller_id: seller_id,
        //             },
        //             {
        //                 withCredentials: true,
        //                 headers: {
        //                     Authorization: `Bearer ${session.access_token}`,
        //                 },
        //             }
        //         )
        //         .then(
        //             onResolve => {
        //                 // TODO: ked bdue api pripravene odstranit premennu {data: ...}
        //                 return {data: onResolve.reservations};
        //             },
        //             onReject => {
        //                 console.log("fetch data error");
        //                 console.log(onReject);
        //             }
        //         )
        //         .catch(error => {
        //             if (error.name === "CanceledError") {
        //                 console.log("Request aborted:", error.message);
        //             } else {
        //                 console.error(error);
        //             }
        //             return error;
        //         });
        // };
    };

    static fetchReservationsLogout = () => {
        return async dispatch => {
            dispatch(BookingLoginAction.clearReservations());
            return dispatch(SessionAction.logout());
        };
    };

    static clearReservations = () => {
        return createAction("BookingLoginAction.ClearBookingLogin", []);
    };
    static setReservations = (reservations: Reservation[]) => {
        return createAction("BookingLoginAction.SetReservations", reservations);
    };
    static setUser = (user: AccountDriver) => {
        return createAction("BookingLoginAction.SetUser", user);
    };

    static setReservation = (reservation: Reservation) => {
        return createAction("BookingLoginAction.SetReservation", reservation);
    };
}

export default BookingLoginAction;
